import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getResults, getQuizzes, getQuizStats, exportCSV, exportXLSX, deleteResult, getResultDetail, getTeacherClasses } from '../lib/api';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Download, BarChart3, Trash2, Eye, FileSpreadsheet, ChevronRight, GraduationCap, Users, Loader2 } from 'lucide-react';
import { SortableHead, sortResults } from '../components/results/SortableHead';
import { ResultsStatsCards } from '../components/results/ResultsStatsCards';
import { ResultDetailModal } from '../components/results/ResultDetailModal';
import { usePageTitle } from '../hooks/usePageTitle';

const gradeColor = (note) => note >= 16 ? 'text-emerald-400' : note >= 12 ? 'text-blue-400' : note >= 10 ? 'text-amber-400' : 'text-red-400';
const gradeBg = (note) => note >= 16 ? 'bg-emerald-400/10' : note >= 12 ? 'bg-blue-400/10' : note >= 10 ? 'bg-amber-400/10' : 'bg-red-400/10';

export default function Results() {
  usePageTitle('Résultats');
  const [searchParams] = useSearchParams();
  const [quizId, setQuizId] = useState(searchParams.get('quiz') || '');
  const [quizzes, setQuizzes] = useState([]);
  const [results, setResults] = useState([]);
  const [sort, setSort] = useState({ key: 'scanned_at', dir: 'desc' });
  const sortedResults = useMemo(() => sortResults(results, sort), [results, sort]);
  const toggleSort = (key) => setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'scanned_at' ? 'desc' : 'asc' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try { setQuizzes((await getQuizzes()).data.data); } catch { toast.error('Erreur de chargement des QCM'); }
    };
    const loadClasses = async () => {
      try { setTeacherClasses((await getTeacherClasses()).data || []); } catch (e) { console.error('Failed to load classes:', e); }
      setClassesLoading(false);
    };
    load();
    loadClasses();
  }, []);

  const effectiveQuizId = quizId && quizId !== 'all' ? quizId : '';

  const loadResults = useCallback(async () => {
    setLoading(true);
    try {
      setResults((await getResults(effectiveQuizId || undefined)).data.data);
      setStats(effectiveQuizId ? (await getQuizStats(effectiveQuizId)).data : null);
    } catch { toast.error('Erreur de chargement des résultats'); }
    setLoading(false);
  }, [effectiveQuizId]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const handleExport = async () => {
    if (!effectiveQuizId) { toast.error('Sélectionnez un QCM pour exporter'); return; }
    try {
      const res = await exportCSV(effectiveQuizId);
      saveAs(new Blob([res.data], { type: 'text/csv;charset=utf-8' }), `notes_${quizId}.csv`);
      toast.success('Export CSV lancé');
    } catch { toast.error('Erreur export CSV'); }
  };

  const handleExportXlsx = async () => {
    if (!effectiveQuizId) { toast.error('Sélectionnez un QCM pour exporter'); return; }
    try {
      const res = await exportXLSX(effectiveQuizId);
      saveAs(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `notes_${quizId}.xlsx`);
      toast.success('Export Excel lancé');
    } catch { toast.error('Erreur export Excel'); }
  };

  const handleDelete = async (resultId) => {
    try { await deleteResult(resultId); toast.success('Résultat supprimé'); loadResults(); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleViewDetail = async (resultId) => {
    setDetailLoading(true); setDetailOpen(true);
    try { setSelectedResult((await getResultDetail(resultId)).data); }
    catch { toast.error('Erreur lors du chargement du détail'); setDetailOpen(false); }
    setDetailLoading(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="results-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Résultats</h1>
          <p className="text-muted-foreground mt-1">{results.length} copies corrigées</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-xlsx-btn" onClick={handleExportXlsx} className="bg-emerald-600 text-white hover:bg-emerald-700">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Exporter Excel
          </Button>
          <Button data-testid="export-csv-btn" onClick={handleExport} variant="secondary" className="border border-white/5">
            <Download className="w-4 h-4 mr-2" /> Exporter CSV
          </Button>
        </div>
      </div>

      {/* Suivi pédagogique - class cards */}
      {!classesLoading && teacherClasses.length > 0 && (
        <Card className="bg-card border-border" data-testid="tracking-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary" /> Suivi pédagogique</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {teacherClasses.map(c => (
                <Link key={c.class_id} to={`/tracking/${c.class_id}`} data-testid={`tracking-card-${c.class_id}`}>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border hover:border-primary/50 transition-all hover:translate-y-[-1px] cursor-pointer">
                    <BarChart3 className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.nom}</p>
                      <p className="text-xs text-muted-foreground">{c.niveau || ''} {c.annee || ''}</p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Select value={quizId} onValueChange={setQuizId}>
        <SelectTrigger className="w-72" data-testid="results-quiz-filter">
          <SelectValue placeholder="Tous les QCM" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les QCM</SelectItem>
          {quizzes.map(q => <SelectItem key={q.quiz_id} value={q.quiz_id}>{q.titre} ({q.classe})</SelectItem>)}
        </SelectContent>
      </Select>

      <ResultsStatsCards stats={stats} />

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {results.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <SortableHead label="Prénom" sortKey="student_prenom" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Nom" sortKey="student_nom" sort={sort} onSort={toggleSort} />
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Classe</TableHead>
                  <SortableHead label="Note" sortKey="note" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Correct" sortKey="total_correct" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Date" sortKey="scanned_at" sort={sort} onSort={toggleSort} />
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedResults.map((r, i) => (
                  <TableRow key={r.result_id || i} className={`hover:bg-muted/30 transition-colors ${r.status === 'erreur_identification' ? 'bg-red-500/5' : ''}`} data-testid={`result-row-${i}`}>
                    <TableCell className="font-medium" data-testid={`result-prenom-${i}`}>{r.student_prenom || '?'}</TableCell>
                    <TableCell className="font-medium" data-testid={`result-nom-${i}`}>
                      {r.student_nom || '?'}
                      {r.status === 'erreur_identification' && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">Non identifié</span>}
                      {r.match_method === 'prenom_nom' && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Via nom</span>}
                    </TableCell>
                    <TableCell><span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary">{r.classe}</span></TableCell>
                    <TableCell>
                      {r.status === 'erreur_identification'
                        ? <span className="text-sm font-bold text-red-400">Erreur</span>
                        : <span className={`text-lg font-black px-2 py-1 rounded ${gradeBg(r.note)} ${gradeColor(r.note)}`}>{r.note}/{r.bareme}</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.status === 'erreur_identification'
                        ? <span className="text-xs text-red-400">{r.status_message || 'Élève non trouvé'}</span>
                        : <>{r.total_correct}/{r.total_questions}</>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.scanned_at ? new Date(r.scanned_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetail(r.result_id)} data-testid={`view-result-${r.result_id}`} className="text-muted-foreground hover:text-primary" title="Voir le détail">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`delete-result-${r.result_id}`} className="text-muted-foreground hover:text-destructive" title="Supprimer">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-border">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer le résultat</AlertDialogTitle>
                              <AlertDialogDescription>Supprimer le résultat de {r.student_name} ({r.note}/{r.bareme}) ?</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(r.result_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-result-${r.result_id}`}>Supprimer</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
              <p className="text-muted-foreground">Aucun résultat. Scannez des copies pour voir les notes.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <ResultDetailModal open={detailOpen} onOpenChange={setDetailOpen} loading={detailLoading} result={selectedResult} />
    </div>
  );
}
